/**
 * Runtime master switch for the phantom-vehicle feature.
 *
 * Source of truth: `config/phantomEnabled` in Realtime Database. The admin
 * toggles it from the admin dashboard. We subscribe with `.on('value')` so
 * a flip propagates to every connected client in real time without needing
 * a fresh APK / page reload.
 *
 * Defaults to `true` until the first server snapshot arrives — so a brand
 * new install / cold start gets the feature immediately, not "disabled
 * because we don't know yet".
 *
 * Public API:
 *   - isEnabled()       — synchronous current state
 *   - init()            — wire the RTDB listener (call once on bootstrap)
 *   - onChange(fn)      — subscribe to live changes
 */

import { ref } from '../../core/firebase.js';
import { createLogger } from '../../core/logger.js';
import * as store from './phantom.store.js';

const log = createLogger('phantom.config');

const RTDB_PATH = 'config/phantomEnabled';

let enabled = true;
let initialised = false;
/** @type {Set<(v: boolean) => void>} */
const listeners = new Set();

/** Wire the RTDB listener. Idempotent. */
export function init() {
  if (initialised) return;
  initialised = true;
  try {
    ref(RTDB_PATH).on('value', (snap) => {
      const v = snap.val();
      // Anything other than an explicit `false` is treated as enabled —
      // including a missing node (first-time deploy where admin hasn't
      // configured the flag yet).
      const next = v !== false;
      if (next !== enabled) {
        const wasEnabled = enabled;
        enabled = next;
        // `console.error` deliberately — legacy `connection-manager.js`
        // silences console.log/info/warn in production mode, so plain
        // logs would be invisible on the deployed site and we'd have no
        // way to tell whether this code path even runs.
        console.error('[phantom.config] phantomEnabled →', enabled);

        // When the admin DISABLES phantom, wipe the local store entirely.
        // Otherwise plates that were previously marked as phantom would
        // stay flagged in localStorage and continue to route real calls /
        // messages through the simulated flow — preventing the user from
        // ever reaching the real owner once a plate is registered.
        if (wasEnabled && !enabled) {
          try {
            store.clearAll();
            console.error('[phantom.config] store cleared (admin disabled)');
          } catch (err) {
            log.warn('clearAll on disable failed:', /** @type {Error} */ (err).message);
          }
        }

        listeners.forEach((fn) => { try { fn(enabled); } catch { /* ignore */ } });
      }
    });
  } catch (err) {
    log.warn('listener wire failed:', /** @type {Error} */ (err).message);
  }
}

/** @returns {boolean} */
export function isEnabled() {
  return enabled;
}

/**
 * Subscribe to live changes of the flag. Returns an unsubscribe function.
 *
 * @param {(value: boolean) => void} fn
 */
export function onChange(fn) {
  if (typeof fn !== 'function') return () => {};
  listeners.add(fn);
  return () => listeners.delete(fn);
}
